import { Badge, Button, DatePicker, Flex, Form, Input, message, Radio, Space, Tag, Upload } from 'antd';
import { ColumnsType } from 'antd/es/table';
import styles from './index.module.less';
import { timeFormatToYearSecond } from '@/utils/date';
import { useRequest, useSetState } from 'ahooks';
import CommonModalFactory from '@/plugins/ModalFactory';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchInput from '@/components/SearchInput';
import markdownConfig from '../Article/markdownConfig';
import {
  analysisApi,
  CompanyAnalysisPageRes,
  EAnalysisPublishStatus,
  EAnalysisReportType,
  EAnalysisStatus,
} from '@/axios/analysis';
import { openConfirmModal, openDeleteModal } from '@/components/Modal/common';
import OperatorBtns from '@/components/Table/operateBtns';
import CommonTable from '@/components/Table';
import EllipsisText from '@/components/Table/EllipsisText';
import { companyApi } from '@/axios/company';
import AccessButton from '@/components/AccessButton';
import MonitorTaskSelector from '@/components/Form/MonitorTaskSelector';
import useResetToFirstPageOrRefresh from '@/hooks/useResetToFirstPageOrRefresh';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import { openScheduledReportModal } from '../ScheduledReportModal';
import ScheduledReportTable from '../ScheduledReportTable';
import { getDocument, GlobalWorkerOptions, TextLayer, type PDFDocumentProxy } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url';

GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const analysisReportTypeMap: Record<
  EAnalysisReportType,
  {
    label: string;
    color?: string;
  }
> = {
  [EAnalysisReportType.Unset]: {
    label: '未设置',
  },
  [EAnalysisReportType.Daily]: {
    label: '日报',
    color: 'blue',
  },
  [EAnalysisReportType.Weekly]: {
    label: '周报',
    color: 'orange',
  },
  [EAnalysisReportType.Monthly]: {
    label: '月报',
    color: 'purple',
  },
};

const getPublishStatusText = (publishStatus?: CompanyAnalysisPageRes['publishStatus']) => {
  if (publishStatus === EAnalysisPublishStatus.Published || publishStatus === 1) {
    return '已发布';
  }
  if (publishStatus === EAnalysisPublishStatus.Unpublished || publishStatus === 0) {
    return '未发布';
  }
  return '-';
};

type AnalysisListParams = {
  pageIndex: number;
  pageSize: number;
  companyId: string;
  search: string;
};
type AnalysisListParamsUpdater = (params: Partial<AnalysisListParams>) => void;

const AnalysisTable = forwardRef(
  (
    {
      from = 'company',
      btns,
      externalParams,
      onParamsChange,
      buildCompanyDetailUrl,
      companyInfo,
    }: {
      from?: 'all' | 'company';
      btns?: React.ReactNode;
      externalParams?: AnalysisListParams;
      onParamsChange?: AnalysisListParamsUpdater;
      buildCompanyDetailUrl?: (companyId: string | number) => string;
      companyInfo?: { companyId?: string | number; companyName?: string };
    },
    ref: any
  ) => {
    const id = new URLSearchParams(window.location.search).get('companyId');
    const currentCompanyId = companyInfo?.companyId ?? id ?? '';
    const currentCompanyName = companyInfo?.companyName;
    const [scheduleRefreshKey, setScheduleRefreshKey] = useState(0);
    const navigate = useNavigate();
    const { data: analysisReport, runAsync: refreshAnalysisReport } = useRequest(
      () => companyApi.getAnalysisReport({ companyId: id || '' }),
      {
        manual: from !== 'company',
      }
    );
    const [innerSearchParams, setInnerSearchParams] = useSetState<AnalysisListParams>({
      pageIndex: 1,
      pageSize: 10,
      companyId: id || '',
      search: '',
    });
    const updateInnerSearchParams: AnalysisListParamsUpdater = (params) => {
      setInnerSearchParams(params as Partial<AnalysisListParams> & AnalysisListParams);
    };
    const searchParams = externalParams || innerSearchParams;
    const setSearchParams = onParamsChange || updateInnerSearchParams;
    const {
      data: list,
      loading,
      runAsync: refreshList,
    } = useRequest(
      () => {
        return analysisApi.list(searchParams);
      },
      {
        refreshDeps: [searchParams],
      }
    );
    const resetToFirstPageOrRefresh = useResetToFirstPageOrRefresh(searchParams, setSearchParams, refreshList);
    const columns: ColumnsType<CompanyAnalysisPageRes> = [
      ...(from === 'all'
        ? [
            {
              title: '企业',
              dataIndex: 'companyName',
              ellipsis: true,
              render: (value: any, record: CompanyAnalysisPageRes) => (
                <a
                  onClick={() => {
                    navigate(buildCompanyDetailUrl?.(record.companyId ?? '') || `/analysis?companyId=${record.companyId ?? ''}`);
                  }}
                >
                  <EllipsisText text={value} />
                </a>
              ),
            },
          ]
        : []),
      {
        title: '名称',
        dataIndex: 'analysisName',
        // ellipsis: true,
        // width: 400,
        render: (value) => <EllipsisText text={value} />,
      },
      {
        title: '报告类型',
        dataIndex: 'reportCycle',
        width: 120,
        render: (value: CompanyAnalysisPageRes['reportCycle']) => {
          const config = analysisReportTypeMap[value || EAnalysisReportType.Unset];
          return <Tag color={config.color}>{config.label}</Tag>;
        },
      },
      {
        title: '状态',
        dataIndex: 'status',
        width: 130,
        render: (value, _) => {
          if (value === EAnalysisStatus.Complete) {
            return <Badge status="success" text="完成" />;
          }
          if (value === EAnalysisStatus.Running) {
            return <Badge status="processing" text="进行中" />;
          }
          if (value === EAnalysisStatus.Fail) {
            return <Badge status="error" text="失败" />;
          }
          if (value === EAnalysisStatus.Invalidated) {
            return <Badge status="default" text="失效" />;
          }
          if (value === EAnalysisStatus.RetryWait) {
            return <Badge status="warning" text="等待重试" />;
          }
          return '-';
        },
      },
      {
        title: '生成时间',
        dataIndex: 'createTime',
        width: 120,
        render: (value) => <div style={{ whiteSpace: 'pre-wrap' }}>{timeFormatToYearSecond(value)}</div>,
      },
      {
        title: '发布状态',
        dataIndex: 'publishStatus',
        width: 100,
        render: (value) => getPublishStatusText(value),
      },
      {
        title: '任务名称',
        dataIndex: 'preTask',
        render: (value, record) => {
          const preTaskName = `优化前任务：${record.preTask?.taskName || ''}`;
          const postTaskName = `优化后任务：${record.postTask?.taskName || ''}`;
          return (
            <EllipsisText
              text={`${preTaskName}\n${postTaskName}`}
              tooltip={
                <>
                  {preTaskName}
                  <br />
                  {postTaskName}
                </>
              }
            />
          );
        },
      },
      {
        title: '操作',
        dataIndex: 'op',
        width: 380,
        render: (value, record) => {
          const canTogglePublish = record.status === EAnalysisStatus.Complete;
          return (
            <OperatorBtns
              btns={[
                {
                  type: 'edit',
                  onClick: () => {
                    openEditAnalysisModal(
                      {
                        analysisId: record.analysisId,
                      },
                      undefined,
                      () => {
                        refreshList();
                      }
                    );
                  },
                  permission: ['company:analysis:view'],
                },
                {
                  type: 'common',
                  text:
                    record.publishStatus === EAnalysisPublishStatus.Published || record.publishStatus === 1
                      ? '取消发布'
                      : '发布',
                  onClick: async () => {
                    if (record.publishStatus === EAnalysisPublishStatus.Published || record.publishStatus === 1) {
                      await analysisApi.unpublish({ analysisIds: [record.analysisId] });
                      message.success('取消发布成功');
                    } else {
                      await analysisApi.publish({ analysisIds: [record.analysisId] });
                      message.success('发布成功');
                    }
                    refreshList();
                  },
                  show: canTogglePublish,
                  permission: ['company:analysis:edit'],
                },
                {
                  type: 'common',
                  text: '更新报告至企业',
                  onClick: async () => {
                    await companyApi.updateReportSetting({
                      companyId: from === 'company' ? (id as string) : record.companyId!,
                      reportId: record.analysisId,
                    });
                    message.success('更新成功');
                    await refreshAnalysisReport();
                    refreshList();
                  },
                  permission: ['company:view:update_report:setting'],
                },
                {
                  type: 'common',
                  text: '下载',
                  onClick: async () => {
                    const downloadKey = await analysisApi.export({ analysisIds: [record.analysisId] });
                    window.open(`/api/common/download/${downloadKey}`, '_blank');
                  },
                  permission: ['company:analysis:export'],
                },
                {
                  type: 'delete',
                  show: from === 'company',
                  onClick: async () => {
                    openDeleteModal(
                      {
                        api: () => analysisApi.delete({ analysisIds: [record.analysisId] }),
                        content: `确定删除分析「${record.analysisName}」吗？`,
                      },
                      undefined,
                      refreshList
                    );
                  },
                  permission: ['company:analysis:delete'],
                },
                {
                  type: 'common',
                  text: '查看企业',
                  show: from === 'all',
                  onClick: () => {
                    navigate(buildCompanyDetailUrl?.(record.companyId ?? '') || `/analysis?companyId=${record.companyId ?? ''}`);
                  },
                  permission: ['company:view'],
                },
              ]}
            />
          );
        },
      },
    ];
    useImperativeHandle(ref, () => ({
      refreshList: (stayCurrent = false) => {
        if (!stayCurrent) {
          resetToFirstPageOrRefresh();
        } else {
          refreshList();
        }
      },
    }));
    return (
      <Flex vertical gap={20}>
        <Flex gap={8} align="center">
          <AccessButton permission={['company:analysis:create']}>
            <Button
              type="primary"
              onClick={() =>
                openCreateAnalysisReport(
                  {
                    companyId: String(currentCompanyId),
                  },
                  undefined,
                  () => {
                    refreshList();
                  }
                )
              }
            >
              创建分析报告
            </Button>
          </AccessButton>
          {from === 'company' && currentCompanyId ? (
            <AccessButton permission={['company:analysis:upload']}>
              <Button
                type="primary"
                onClick={() =>
                  openUploadAnalysisReport(
                    {
                      companyId: String(currentCompanyId),
                      companyName: currentCompanyName,
                    },
                    undefined,
                    () => {
                      refreshList();
                      refreshAnalysisReport();
                    }
                  )
                }
              >
                +上传报告
              </Button>
            </AccessButton>
          ) : null}
          {from === 'company' && currentCompanyId ? (
            <AccessButton permission={['company:analysis:create']}>
              <Button type="primary" onClick={() => openScheduledReportModal({ companyId: String(currentCompanyId) }, undefined, () => {
                setScheduleRefreshKey(value => value + 1);
                refreshList();
              })}>创建定时报告</Button>
            </AccessButton>
          ) : null}
          {from === 'company' && analysisReport?.reportId && (
            <Space>
              客户报表使用的报告：
              <a
                onClick={() => {
                  openEditAnalysisModal(
                    {
                      analysisId: analysisReport.reportId!,
                      readOnly: true,
                    },
                    {
                      title: '查看分析报告',
                    }
                  );
                }}
              >
                {analysisReport?.title}
              </a>
              {new Date(analysisReport?.createTime).toLocaleString()}
              <AccessButton permission={['company:view:update_report:setting']}>
                <Button
                  type="primary"
                  onClick={() => {
                    openConfirmModal({
                      confirmApi: async () => {
                        await companyApi.updateReportSetting({
                          companyId: id as string,
                          reportId: null,
                        });
                        message.success('已移除分析报告');
                        refreshAnalysisReport();
                        refreshList();
                      },
                      content: '确定要移除企业看板上的分析报告吗？',
                    });
                  }}
                >
                  移除
                </Button>
              </AccessButton>
              {/* <SVGIcon
                icon={<CloseIcon />}
                onClick={() => {
                  openConfirmModal({
                    confirmApi: async () => {
                      await companyApi.updateReportSetting({
                        companyId: id as string,
                        reportId: null,
                      });
                      message.success('已移除分析报告');
                      refreshAnalysisReport();
                      refreshList();
                    },
                    content: '确定要移除企业看板上的分析报告吗？',
                  });
                }}
              /> */}
            </Space>
          )}
          <SearchInput
            style={{ width: '200px', marginLeft: 'auto' }}
            placeholder={from === 'company' ? '请输入分析任务名称' : '搜索分析任务名称/企业名称'}
            defaultValue={searchParams.search}
            onSearch={(val) => {
              setSearchParams({ search: val, pageIndex: 1 });
            }}
          />
        </Flex>
        <CommonTable<CompanyAnalysisPageRes>
          loading={loading}
          rowKey={'analysisId'}
          columns={columns}
          dataSource={list?.records || []}
          total={list?.total}
          params={searchParams}
          setParams={setSearchParams}
          scroll={{ x: '1000px' }}
        />
        {from === 'company' && currentCompanyId ? (
          <AccessButton permission={['company:analysis_task:view']}>
            <ScheduledReportTable companyId={String(currentCompanyId)} refreshKey={scheduleRefreshKey}
              onReport={(analysisId) => openEditAnalysisModal({ analysisId, readOnly: true }, { title: '查看分析报告' })} />
          </AccessButton>
        ) : null}
      </Flex>
    );
  }
);
export default AnalysisTable;
export const openEditAnalysisModal = CommonModalFactory<{
  content: string;
  analysisId?: string;
  analysisName: string;
  reportCycle?: EAnalysisReportType;
  readOnly?: boolean;
  sourceFileUrl?: string;
  sourceFileName?: string;
  sourceFileType?: string;
  sourceFileSize?: number;
  previewFileUrl?: string;
  previewFileType?: string;
}>(
  {
    title: '编辑分析结果',
    width: 1000,
    maskClosable: false,
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    const cherryInstance = useRef<any>();
    useEffect(() => {
      form.setFieldsValue({
        analysisName: data?.analysisName,
        reportCycle: data?.reportCycle || EAnalysisReportType.Unset,
      });
      if (data?.sourceFileUrl) {
        return;
      }
      cherryInstance.current = new window.Cherry({
        ...markdownConfig,
        isPreviewOnly: data?.readOnly,
        value: data?.content,
        event: {
          afterChange: () => {},
        },
      } as any);
    }, [data?.analysisName, data?.content, data?.readOnly, data?.sourceFileUrl]);
    useEffect(() => {
      setOkFn(async () => {
        if (data?.readOnly) {
          return;
        }
        const values = await form.validateFields();
        const markdown = data?.sourceFileUrl ? data?.content || '' : cherryInstance.current?.getMarkdown();
        await analysisApi.update({
          analysisId: data?.analysisId as string,
          analysisName: values.analysisName,
          reportCycle: values.reportCycle,
          content: markdown || '',
        });
      });
    }, [data, form, setOkFn]);
    return (
      <Form form={form} disabled={data?.readOnly}>
        <Flex vertical gap={8}>
          <Form.Item name="analysisName" label="名称" rules={[{ required: true, message: '请输入分析名称' }]}>
            <Input placeholder="请输入分析名称" />
          </Form.Item>
          <Form.Item name="reportCycle" label="报告类型">
            <Radio.Group
              options={[
                {
                  value: EAnalysisReportType.Unset,
                  label: '未设置',
                },
                {
                  value: EAnalysisReportType.Daily,
                  label: '日报',
                },
                {
                  value: EAnalysisReportType.Weekly,
                  label: '周报',
                },
                {
                  value: EAnalysisReportType.Monthly,
                  label: '月报',
                },
              ]}
            />
          </Form.Item>
          {data?.sourceFileUrl ? (
            <AnalysisSourceFileViewer
              analysisId={data.analysisId}
              sourceFileUrl={data.sourceFileUrl}
              sourceFileName={data.sourceFileName}
              sourceFileType={data.sourceFileType}
              previewFileUrl={data.previewFileUrl}
              previewFileType={data.previewFileType}
            />
          ) : (
            <div id="markdown-container" className={styles.markdown}></div>
          )}
        </Flex>
      </Form>
    );
  },
  async ({ analysisId, ...props }) => {
    const res = await analysisApi.get(analysisId!);
    return {
      ...props,
      content: res.content,
      analysisName: res.analysisName,
      reportCycle: res.reportCycle,
      analysisId,
      sourceFileUrl: res.sourceFileUrl,
      sourceFileName: res.sourceFileName,
      sourceFileType: res.sourceFileType,
      sourceFileSize: res.sourceFileSize,
      previewFileUrl: res.previewFileUrl,
      previewFileType: res.previewFileType,
    };
  }
);

const canPreviewAnalysisSourceFile = (sourceFileType?: string) => {
  const fileType = String(sourceFileType || '').toLowerCase();
  return fileType === 'pdf' || fileType === 'html';
};

const AnalysisSourceFileViewer = ({
  analysisId,
  sourceFileUrl,
  sourceFileName,
  sourceFileType,
  previewFileUrl,
  previewFileType,
}: {
  analysisId?: string;
  sourceFileUrl: string;
  sourceFileName?: string;
  sourceFileType?: string;
  previewFileUrl?: string;
  previewFileType?: string;
}) => {
  const fileType = String(sourceFileType || '').toLowerCase();
  const previewType = String(previewFileType || '').toLowerCase();
  const hasHtmlPreview = previewType === 'html' && Boolean(previewFileUrl);
  const canPreview = Boolean(analysisId) && (hasHtmlPreview || canPreviewAnalysisSourceFile(fileType));
  const resolvedPreviewUrl = canPreview ? `/api/company/analysis/${analysisId}/preview` : '';
  return (
    <div className={styles.analysisSourceFile}>
      <div className={styles.analysisSourceFileHeader}>
        <div>
          <div className={styles.analysisSourceFileName}>{sourceFileName || '报告文件'}</div>
          <div className={styles.analysisSourceFileType}>{fileType ? fileType.toUpperCase() : 'FILE'}</div>
        </div>
        <Button type="primary" onClick={() => window.open(sourceFileUrl, '_blank')}>
          {canPreview ? '打开报告' : '下载报告'}
        </Button>
      </div>
      {fileType === 'pdf' && resolvedPreviewUrl ? (
        <PdfSourceFilePreview
          previewEndpoint={resolvedPreviewUrl}
          sourceFileName={sourceFileName}
          sourceFileUrl={sourceFileUrl}
        />
      ) : canPreview ? (
        <iframe
          className={styles.analysisSourceFilePreview}
          title={sourceFileName || '报告文件'}
          src={resolvedPreviewUrl}
          sandbox={previewType === 'html' || fileType === 'html' ? 'allow-same-origin allow-scripts' : undefined}
        />
      ) : (
        <div className={styles.analysisSourceFileHint}>该格式暂不支持在线预览，请下载后查看。</div>
      )}
    </div>
  );
};

const PdfSourceFilePreview = ({
  previewEndpoint,
  sourceFileName,
  sourceFileUrl,
}: {
  previewEndpoint: string;
  sourceFileName?: string;
  sourceFileUrl: string;
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    container.innerHTML = '';
    setStatus('loading');

    const renderPdf = async () => {
      try {
        const response = await fetch(previewEndpoint, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error('Failed to load PDF preview');
        }

        const data = await response.arrayBuffer();
        const pdf = await getDocument({ data }).promise;
        await renderAnalysisPdfPages(pdf, container, () => cancelled);

        if (!cancelled) {
          setStatus('ready');
        }
      } catch (error) {
        if (!cancelled && !controller.signal.aborted) {
          setStatus('error');
        }
      }
    };

    void renderPdf();

    return () => {
      cancelled = true;
      controller.abort();
      container.innerHTML = '';
    };
  }, [previewEndpoint]);

  return (
    <div className={styles.analysisSourcePdfPreviewWrap}>
      {status === 'loading' ? <div className={styles.analysisSourceFileHint}>报告加载中...</div> : null}
      {status === 'error' ? (
        <div className={styles.analysisSourceFileHint}>
          <span>PDF 预览加载失败，请下载后查看。</span>
          <Button type="primary" onClick={() => window.open(sourceFileUrl, '_blank')}>
            下载报告
          </Button>
        </div>
      ) : null}
      <div
        ref={containerRef}
        className={styles.analysisSourcePdfPreview}
        aria-label={sourceFileName || 'PDF report preview'}
        hidden={status === 'error'}
      />
    </div>
  );
};

const renderAnalysisPdfPages = async (pdf: PDFDocumentProxy, container: HTMLDivElement, isCancelled: () => boolean) => {
  const outputScale = Math.min(window.devicePixelRatio || 1, 2);
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
    if (isCancelled()) {
      return;
    }

    const page = await pdf.getPage(pageNumber);
    const baseViewport = page.getViewport({ scale: 1 });
    const availableWidth = Math.max(container.clientWidth, 320);
    const scale = availableWidth / baseViewport.width;
    const viewport = page.getViewport({ scale });
    const pageLayer = document.createElement('div');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas is not supported');
    }

    pageLayer.className = styles.analysisSourcePdfPageLayer;
    pageLayer.style.width = `${viewport.width}px`;
    pageLayer.style.height = `${viewport.height}px`;
    canvas.width = Math.floor(viewport.width * outputScale);
    canvas.height = Math.floor(viewport.height * outputScale);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    canvas.className = styles.analysisSourcePdfPage;
    await page.render({
      canvas,
      canvasContext: context,
      viewport,
      transform: outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0],
    }).promise;
    const textLayerContainer = document.createElement('div');
    textLayerContainer.className = styles.analysisSourcePdfTextLayer;
    pageLayer.appendChild(canvas);
    pageLayer.appendChild(textLayerContainer);
    const textLayer = new TextLayer({
      textContentSource: page.streamTextContent(),
      container: textLayerContainer,
      viewport,
    });
    await textLayer.render();

    if (!isCancelled()) {
      container.appendChild(pageLayer);
    }
  }
};

export const openUploadAnalysisReport = CommonModalFactory<{
  companyId: string;
  companyName?: string;
}>(
  {
    title: '上传报告',
    width: 640,
    okText: '确认上传',
    cancelText: '取消',
    maskClosable: false,
  },
  ({ data, setOkFn, update }) => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [uploading, setUploading] = useState(false);
    const selectedFile = fileList[0];
    const supportedFileTypes = ['pdf', 'html', 'docx'];
    const maxUploadFileSizeMb = 20;

    useEffect(() => {
      setOkFn(async () => {
        const file = selectedFile?.originFileObj as File | undefined;
        if (!file) {
          message.warning('请上传报告文件');
          throw new Error('请上传报告文件');
        }
        const values = await form.validateFields();
        const formData = new FormData();
        formData.append('file', file);
        formData.append('companyId', data.companyId);
        formData.append('analysisName', String(values.analysisName || '').trim());
        formData.append('reportCycle', values.reportCycle || EAnalysisReportType.Unset);
        formData.append('generateTime', dayjs(values.generateTime || dayjs()).format('YYYY-MM-DD HH:mm:ss'));
        setUploading(true);
        update({ confirmLoading: true });
        try {
          await analysisApi.upload(formData);
          message.success('上传成功');
        } finally {
          setUploading(false);
          update({ confirmLoading: false });
        }
      });
    }, [data.companyId, form, selectedFile, setOkFn, update]);

    const beforeUpload = (file: File) => {
      const fileType = file.name.split('.').pop()?.toLowerCase();
      if (!fileType || !supportedFileTypes.includes(fileType)) {
        message.warning('仅支持上传 pdf、html、docx 格式文件');
        return Upload.LIST_IGNORE;
      }
      if (file.size > maxUploadFileSizeMb * 1024 * 1024) {
        message.warning(`仅支持上传 ${maxUploadFileSizeMb}MB 以内的文件`);
        return Upload.LIST_IGNORE;
      }
      return false;
    };

    return (
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          reportCycle: EAnalysisReportType.Unset,
          generateTime: dayjs(),
        }}
      >
        <div className={styles.uploadReportCompany}>当前企业：{data.companyName || data.companyId}</div>
        <div className={styles.uploadReportNotice}>上传后报告将保持未发布状态，且不会自动更新到客户报表。</div>
        <Form.Item required label="上传文件">
          <Upload.Dragger
            accept=".pdf,.html,.docx"
            maxCount={1}
            fileList={fileList}
            beforeUpload={beforeUpload}
            onChange={({ fileList: nextFileList }) => setFileList(nextFileList.slice(-1))}
            onRemove={() => setFileList([])}
            disabled={uploading}
          >
            <div className={styles.uploadReportDraggerText}>点击或拖拽文件到此处上传</div>
            <div className={styles.uploadReportDraggerHint}>支持 pdf、html、docx 格式，{maxUploadFileSizeMb}MB 以内，一次只能上传一个文件</div>
          </Upload.Dragger>
        </Form.Item>
        <Form.Item name="analysisName" label="名称" rules={[{ required: true, message: '请输入报告名称' }]}>
          <Input placeholder="请输入报告名称" />
        </Form.Item>
        <Form.Item name="reportCycle" label="报告类型" rules={[{ required: true, message: '请选择报告类型' }]}>
          <Radio.Group
            options={[
              {
                value: EAnalysisReportType.Unset,
                label: '未设置',
              },
              {
                value: EAnalysisReportType.Daily,
                label: '日报',
              },
              {
                value: EAnalysisReportType.Weekly,
                label: '周报',
              },
              {
                value: EAnalysisReportType.Monthly,
                label: '月报',
              },
            ]}
          />
        </Form.Item>
        <Form.Item name="generateTime" label="生成时间">
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    );
  }
);

export const openCreateAnalysisReport = CommonModalFactory<{
  companyId: string;
}>(
  {
    title: '创建分析报告',
    width: 500,
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        await companyApi.createAnalysis({
          companyId: data?.companyId as string,
          ...values,
        } as any);
        message.success('创建成功');
      });
    }, [data, setOkFn]);
    const validateAtLeastOne = (_: any, value: any) => {
      const prePerformanceTaskId = form.getFieldValue('prePerformanceTaskId');
      const postPerformanceTaskId = form.getFieldValue('postPerformanceTaskId');
      if (!prePerformanceTaskId && !postPerformanceTaskId) {
        return Promise.reject(new Error('请至少选择一个性能任务'));
      }
      return Promise.resolve();
    };
    return (
      <Form
        form={form}
        initialValues={{
          reportCycle: EAnalysisReportType.Unset,
        }}
      >
        <Form.Item name="reportCycle" label="报告类型">
          <Radio.Group
            options={[
              {
                value: EAnalysisReportType.Unset,
                label: '未设置',
              },
              {
                value: EAnalysisReportType.Daily,
                label: '日报',
              },
              {
                value: EAnalysisReportType.Weekly,
                label: '周报',
              },
              {
                value: EAnalysisReportType.Monthly,
                label: '月报',
              },
            ]}
          />
        </Form.Item>
        <Form.Item
          name="prePerformanceTaskId"
          label="优化前任务"
          rules={[
            {
              validator: validateAtLeastOne,
            },
          ]}
          dependencies={['postPerformanceTaskId']}
        >
          <MonitorTaskSelector
            params={{
              companyId: data?.companyId,
            }}
          />
        </Form.Item>
        <Form.Item
          name="postPerformanceTaskId"
          label="优化后任务"
          rules={[
            {
              validator: validateAtLeastOne,
            },
          ]}
          dependencies={['prePerformanceTaskId']}
        >
          <MonitorTaskSelector
            params={{
              companyId: data?.companyId,
            }}
          />
        </Form.Item>
      </Form>
    );
  }
);
